import type { HovTimBalanceSnapshot } from "../types";
import {
  ceilHugeAtLeast,
  huge,
  hugeOne,
  hugeZero,
  type HugeNumber,
} from "@/shared/numbers";

export const HOV_CELL_COUNT = 9;
export const TIM_CELL_COUNT = 9;

export interface HovCellState {
  unlocked: boolean;
  hovGainMultiplier: HugeNumber;
  upgradeCost: HugeNumber;
  purchasedUpgrades: number;
}

export interface TimCellState {
  unlocked: boolean;
  timGainMultiplier: HugeNumber;
  upgradeCost: HugeNumber;
  purchasedUpgrades: number;
}

export const HOV_CELL_GAIN_UPGRADE_BASE_COST = 100;
export const HOV_CELL_GAIN_MULTIPLIER_INCREMENT = 1;
export const HOV_CELL_GAIN_UPGRADE_COST_MULTIPLIER = 1.3;
export const HOV_CELL_UNLOCK_INITIAL_MULTIPLIER = 1;
export const TIM_CELL_GAIN_UPGRADE_BASE_COST = 10;
export const TIM_CELL_GAIN_MULTIPLIER_INCREMENT = 0.05;
export const TIM_CELL_UNLOCK_INITIAL_MULTIPLIER = 0.05;
export const TIM_CELL_UPGRADE_COST_MULTIPLIER = 1.4;

export const HOV_CELL_BASE_COST = 2;
export const HOV_CELL_BASE_COST_MULTIPLIER_BASE_PER_STEP = 100;

function resolveHovCellBaseCost(index: number): number {
  let cost = HOV_CELL_BASE_COST;

  for (let step = 1; step <= index; step += 1) {
    cost *= HOV_CELL_BASE_COST_MULTIPLIER_BASE_PER_STEP * Math.pow(2, step - 1);
  }

  return cost;
}

export const HOV_CELL_BASE_COSTS: readonly number[] = Array.from(
  { length: HOV_CELL_COUNT },
  (_, index) => resolveHovCellBaseCost(index),
);

// Cell 0 (H1G) is unlocked from the start, so its first upgrade cost is one
// progression step above the base cost.
export function resolveInitialHovCellUpgradeCost(
  index: number,
  baseCost: number,
): HugeNumber {
  if (index === 0) {
    return ceilHugeAtLeast(
      huge(baseCost).mul(HOV_CELL_GAIN_UPGRADE_COST_MULTIPLIER),
      1,
    );
  }
  return huge(baseCost);
}

export function createDefaultHovCells(
  baseCosts: readonly number[] = HOV_CELL_BASE_COSTS,
): HovCellState[] {
  return Array.from({ length: HOV_CELL_COUNT }, (_, index) => ({
    unlocked: index === 0,
    hovGainMultiplier: index === 0 ? hugeOne() : hugeZero(),
    upgradeCost: resolveInitialHovCellUpgradeCost(
      index,
      baseCosts[index] ?? HOV_CELL_GAIN_UPGRADE_BASE_COST,
    ),
    purchasedUpgrades: 0,
  }));
}

export function createDefaultHovGainUnlockCosts(): Array<HugeNumber | null> {
  return Array.from({ length: HOV_CELL_COUNT }, () => null);
}

export const TIM_CELL_BASE_COSTS: readonly number[] = [
  TIM_CELL_GAIN_UPGRADE_BASE_COST,
  80,
  160,
  320,
  640,
  1000,
  1500,
  2200,
  3000,
];

export function createDefaultTimCells(
  baseCosts: readonly number[] = TIM_CELL_BASE_COSTS,
): TimCellState[] {
  return Array.from({ length: TIM_CELL_COUNT }, (_, index) => ({
    unlocked: index === 0,
    timGainMultiplier: index === 0 ? hugeOne() : hugeZero(),
    upgradeCost: huge(baseCosts[index] ?? TIM_CELL_GAIN_UPGRADE_BASE_COST),
    purchasedUpgrades: 0,
  }));
}

export interface HovTimBalanceState {
  total: HovTimBalanceSnapshot["total"];
  unassigned: HovTimBalanceSnapshot["unassigned"];
  hovAllocation: HovTimBalanceSnapshot["hovAllocation"];
  timAllocation: HovTimBalanceSnapshot["timAllocation"];
  hovGainAllocation: HovTimBalanceSnapshot["hovGainAllocation"];
  timGainAllocation: HovTimBalanceSnapshot["timGainAllocation"];
}
